if (!customElements.get("localization-drawer")) {
  class LocalizationDrawer extends HTMLElement {
    constructor() {
      super();

      this.drawerClass = "wt-localization-drawer";
      this.drawer = this;
      this.classDrawerActive = `${this.drawerClass}--open`;
      this.pageOverlayClass = "wt-localization-drawer-overlay";
      this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
      this.body = document.body;

      this.triggerQuery = [
        ".wt-localization-trigger",
        ".wt-localization-drawer__close",
        `.${this.pageOverlayClass}`,
      ].join(", ");

      this.isOpen = false;
      this.mainTrigger =
        document.querySelector(".wt-header__localization-trigger") || null;

      this.lastOpenerEl = null;

      this.selectors = {
        tabContent: ".wt-localization-drawer__tab__content",
        activeTabContent: ".wt-localization-drawer__tab__content.is-active",
        focusable:
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      };
    }

    connectedCallback() {
      this.init();
      this.inert = true;
      this.setAttribute("aria-hidden", "true");
    }

    storeOpenerElement(triggerEl) {
      const candidate = triggerEl || document.activeElement;

      if (candidate && candidate instanceof HTMLElement) {
        this.lastOpenerEl = candidate;
      } else {
        this.lastOpenerEl = null;
      }
    }

    restoreFocusToOpener() {
      const el = this.lastOpenerEl;

      if (!el) return false;
      if (!document.contains(el)) return false;
      if (el.hasAttribute("disabled")) return false;

      try {
        el.focus();
        this.lastOpenerEl = null;
        return true;
      } catch {
        return false;
      }
    }

    getGlobalFocusableElements() {
      const all = Array.from(this.querySelectorAll(this.selectors.focusable));

      return all.filter((el) => {
        if (el.offsetParent === null) return false;
        if (el.closest(this.selectors.tabContent)) return false;
        return true;
      });
    }

    getActiveTabFocusableElements() {
      const activePanel = this.querySelector(this.selectors.activeTabContent);
      if (!activePanel) return [];

      return Array.from(
        activePanel.querySelectorAll(this.selectors.focusable),
      ).filter((el) => el.offsetParent !== null);
    }

    getAllowedFocusableElements() {
      return [
        ...this.getGlobalFocusableElements(),
        ...this.getActiveTabFocusableElements(),
      ];
    }

    temporaryHideFocusVisible() {
      document.body.classList.add("no-focus-visible");
      setTimeout(() => {
        document.body.classList.remove("no-focus-visible");
      }, 200);
    }

    openDrawer() {
      if (this.isOpen) return;

      this.setAttribute("open", "");
      this.isOpen = true;
      this.inert = false;
      this.setAttribute("aria-hidden", "false");
      this.drawer.classList.add(this.classDrawerActive);
      this.body.classList.add(this.activeOverlayBodyClass);
      this.temporaryHideFocusVisible();

      const closeBtn = this.querySelector(".wt-localization-drawer__close");
      if (closeBtn) closeBtn.focus();
    }

    closeDrawer() {
      if (!this.isOpen) return;

      this.removeAttribute("open");
      this.isOpen = false;
      this.inert = true;
      this.setAttribute("aria-hidden", "true");
      this.drawer.classList.remove(this.classDrawerActive);
      this.body.classList.remove(this.activeOverlayBodyClass);
      this.temporaryHideFocusVisible();

      requestAnimationFrame(() => {
        const restored = this.restoreFocusToOpener();
        if (!restored && this.mainTrigger) this.mainTrigger.focus();
      });
    }

    toggleDrawerClasses() {
      if (this.isOpen) {
        this.closeDrawer();
      } else {
        this.openDrawer();
      }
    }

    setActiveTab(tabKey) {
      if (!tabKey) return;

      const tabTriggers = this.querySelectorAll(
        ".wt-localization-drawer__tab__trigger",
      );
      const tabContents = this.querySelectorAll(
        ".wt-localization-drawer__tab__content",
      );

      tabTriggers.forEach((t) =>
        t.classList.remove("wt-localization-drawer__tab__trigger--active"),
      );
      tabContents.forEach((c) => c.classList.remove("is-active"));

      const matchingTrigger = this.querySelector(
        `[data-tab-target="${tabKey}"]`,
      );
      if (matchingTrigger) {
        matchingTrigger.classList.add(
          "wt-localization-drawer__tab__trigger--active",
        );
      }

      const matchingContent = this.querySelector(
        `[data-tab-content="${tabKey}"]`,
      );
      if (matchingContent) {
        matchingContent.classList.add("is-active");
      }
    }

    isCloseTrigger(trigger) {
      return (
        trigger.classList.contains("wt-localization-drawer__close") ||
        trigger.classList.contains(this.pageOverlayClass)
      );
    }

    init() {
      this.addEventListener("keydown", (e) => {
        if (!this.isOpen) return;

        const isTab = e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
        const isEsc =
          e.key === "Escape" || e.keyCode === 27 || e.code === "Escape";

        if (isEsc) {
          e.preventDefault();
          this.closeDrawer();
          return;
        }

        if (!isTab) return;

        const focusables = this.getAllowedFocusableElements();
        if (!focusables.length) return;

        const first = focusables[0];
        const last = focusables[focusables.length - 1];

        if (!focusables.includes(document.activeElement)) {
          e.preventDefault();
          first.focus();
          return;
        }

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      });

      document.querySelectorAll(this.triggerQuery).forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();

          if (this.isCloseTrigger(trigger)) {
            this.closeDrawer();
            return;
          }

          // Country / language opener: switch tab; open if closed (don't toggle closed).
          this.storeOpenerElement(e.currentTarget);
          const openTab = trigger.dataset.openDrawer;
          if (openTab) this.setActiveTab(openTab);
          this.openDrawer();
        });
      });

      const searchInput = this.querySelector(
        ".wt-localization-drawer__search-country__input",
      );
      if (searchInput) {
        searchInput.addEventListener("input", () => {
          const searchValue = searchInput.value.toLowerCase().trim();

          this.querySelectorAll(".country-selector__item").forEach((item) => {
            const countryName = (item.dataset.filterName || "").toLowerCase();
            item.style.display = countryName.includes(searchValue) ? "" : "none";
          });
        });
      }

      const tabTriggers = this.querySelectorAll(
        ".wt-localization-drawer__tab__trigger",
      );
      const tabContents = this.querySelectorAll(
        ".wt-localization-drawer__tab__content",
      );

      tabTriggers.forEach((trigger) => {
        trigger.addEventListener("click", (e) => {
          e.preventDefault();
          tabTriggers.forEach((t) =>
            t.classList.remove("wt-localization-drawer__tab__trigger--active"),
          );
          tabContents.forEach((c) => c.classList.remove("is-active"));

          trigger.classList.add("wt-localization-drawer__tab__trigger--active");
          const targetKey = trigger.dataset.tabTarget;
          const matchedContent = this.querySelector(
            `[data-tab-content="${targetKey}"]`,
          );
          if (matchedContent) {
            matchedContent.classList.add("is-active");
          }
        });
      });

      const countryForm = document.querySelector("#DrawerCountryForm");
      if (countryForm) {
        const countryInput = countryForm.querySelector('[name="country_code"]');
        const countryLinks = this.querySelectorAll(".country-selector__trigger");

        countryLinks.forEach((link) => {
          link.addEventListener("click", (e) => {
            e.preventDefault();
            if (!countryInput) return;
            countryInput.value = link.dataset.value;
            this.submitLocalizationForm(countryForm);
          });
        });
      }

      const languageForm = this.querySelector("form#DrawerLanguageForm");
      if (languageForm) {
        const radioButtons = languageForm.querySelectorAll(
          'input[name="language_code"]',
        );
        radioButtons.forEach((radio) => {
          radio.addEventListener("change", () => {
            this.submitLocalizationForm(languageForm);
          });
        });
      }
    }

    /**
     * Theme-dev (127.0.0.1:9292) often 404s POST /localization.
     * Point the form at the real shop host and return to the preview theme.
     * Never use return_to value "back" — Shopify treats it as the path /back.
     */
    submitLocalizationForm(form) {
      const shop = window.Shopify?.shop;
      const themeId = window.Shopify?.theme?.id;
      const isLocalDev =
        location.hostname === "127.0.0.1" || location.hostname === "localhost";

      const returnInputs = [
        ...form.querySelectorAll('input[name="return_to"]'),
      ];
      let returnInput = returnInputs[0];
      if (!returnInput) {
        returnInput = document.createElement("input");
        returnInput.type = "hidden";
        returnInput.name = "return_to";
        form.appendChild(returnInput);
      }
      returnInputs.slice(1).forEach((el) => el.remove());

      let returnPath = location.pathname || "/";
      if (returnPath === "/back" || returnPath.endsWith("/back")) {
        returnPath = "/";
      }

      if (isLocalDev && shop) {
        form.setAttribute("action", `https://${shop}/localization`);
        const returnUrl = new URL(`https://${shop}${returnPath}`);
        if (themeId) {
          returnUrl.searchParams.set("preview_theme_id", String(themeId));
        }
        returnInput.value = `${returnUrl.pathname}${returnUrl.search}`;
      } else {
        returnInput.value = returnPath + (location.search || "");
      }

      form.submit();
    }
  }

  customElements.define("localization-drawer", LocalizationDrawer);
}
